import get from 'lodash/get';
import { DEVIATION_PATHS } from '@wix/da-url/dist/src/constants';
import { Url } from '@wix/da-url';
import { DeviationTypes, DeviationResourceType, DeviationSuggestionReasons, } from '../types/deviation';
import { getFeature } from '../DaEditorWrapper/Features/helper';
import { MEDIA_COUNT_FEATURE, FIRST_IMAGE_FEATURE, } from '../DaEditorWrapper/Features';
import DeviationMediaService from '../utils/DeviationMediaService';
import { BackgroundImageDisplayMode, BackgroundType, } from '../Background/types';
var CHARS_BEFORE_LONG_STATUS = 110;
export var JOURNAL_CATEGORY_PREFIXES = ['journals', 'personaljournal'];
// for fullviews/art stage
var TINY_IMAGE_THRESHOLD_WIDTH = 400;
var TINY_IMAGE_THRESHOLD_HEIGHT = 400;
var TINY_IMAGE_THRESHOLD_WIDTH_MOBILE = 200;
var TINY_IMAGE_THRESHOLD_HEIGHT_MOBILE = 180;
export function styleFromWidthHeight(_a, responsive) {
    var width = _a.width, height = _a.height;
    if (responsive === void 0) { responsive = true; }
    // if nothing is set, always fill container
    if (!width && !height) {
        return {
            width: '100%',
            height: '100%',
        };
    }
    // if responsive mode, or both props are set - just use whatever is set
    if (responsive || (width && height)) {
        return { width: width, height: height };
    }
    // otherwise try to square it off
    return {
        width: width || height || '100%',
        height: height || width || '100%',
    };
}
export function getDeviationType(deviation) {
    return DeviationMediaService.getDeviationType(deviation);
}
/**
 * Returns a deviation's title. Some deviation types will return `undefined` since they have default placeholder titles.
 *
 * e.g. deviations with type `DeviationTypes.STATUS` will return `undefined` instead of `Untitled`
 */
export function getDeviationTitle(deviation) {
    return getDeviationType(deviation) === DeviationTypes.STATUS
        ? undefined
        : deviation === null || deviation === void 0 ? void 0 : deviation.title;
}
export function getDeviationResourceType(deviation) {
    var type = getDeviationType(deviation);
    switch (type) {
        case DeviationTypes.JOURNAL:
            return DeviationResourceType.JOURNAL;
        case DeviationTypes.POLL:
            return DeviationResourceType.POLL;
        case DeviationTypes.COMMISSION:
            return DeviationResourceType.COMMISSION;
        case DeviationTypes.STATUS:
            return isLegacyStatus(deviation)
                ? DeviationResourceType.STATUS
                : DeviationResourceType.STATUS_DEVIATION;
        case DeviationTypes.TIER:
            return DeviationResourceType.TIER;
        default:
            return DeviationResourceType.ART;
    }
}
export function isVisualDeviation(deviation) {
    return [
        DeviationTypes.FILM,
        DeviationTypes.IMAGE,
        DeviationTypes.PDF,
    ].includes(getDeviationType(deviation));
}
export function isTextualDeviation(deviation) {
    var type = DeviationMediaService.getDeviationType(deviation);
    if ([
        DeviationTypes.JOURNAL,
        DeviationTypes.LITERATURE,
        DeviationTypes.STATUS,
    ].includes(type)) {
        if (type === DeviationTypes.STATUS && isLegacyStatus(deviation)) {
            return false;
        }
        return true;
    }
    return false;
}
export function isLegacyStatus(deviation) {
    if ((deviation === null || deviation === void 0 ? void 0 : deviation.typeId) === DeviationResourceType.STATUS) {
        return true;
    }
    return false;
}
export function isSimpleDeviation(deviation) {
    var isSimpleType = [
        DeviationTypes.COMMISSION,
        DeviationTypes.POLL,
    ].includes(getDeviationType(deviation));
    return isSimpleType || isLegacyStatus(deviation);
}
export function isDeviationAuthor(deviation, user) {
    var authorUserId = get(deviation, 'extended.originalAuthor.userId', get(deviation, 'author.userId', false));
    return authorUserId === (user === null || user === void 0 ? void 0 : user.userId);
}
export function isDeviationPixelArt(deviation) {
    return false; // we don't support pixel detection for now
}
export function isGifDeviation(deviation) {
    return DeviationMediaService.isGif(deviation);
}
export function isVideoDeviation(deviation) {
    return (DeviationMediaService.getDeviationType(deviation) === DeviationTypes.FILM);
}
export function isDeviationTransparent(deviation) {
    // get our representative fullview file
    var fullview = DeviationMediaService.getLargestFullview(deviation);
    // and see if it's transparent
    return !!fullview.transparency;
}
export function isDeviationTextEditable(deviation) {
    var isTextEditable = !!deviation.isTextEditable;
    var hasLegacyTextEditUrl = !!deviation.legacyTextEditUrl;
    switch (getDeviationType(deviation)) {
        case DeviationTypes.LITERATURE:
        case DeviationTypes.JOURNAL:
            return isTextEditable || hasLegacyTextEditUrl;
        case DeviationTypes.COMMISSION:
        case DeviationTypes.POLL:
        case DeviationTypes.STATUS:
            return isTextEditable;
        default:
            return false;
    }
}
export var isImageDeviation = function (deviation) {
    return getDeviationType(deviation) === DeviationTypes.IMAGE;
};
export function getDeviationBasePath(deviation) {
    switch (getDeviationType(deviation)) {
        case DeviationTypes.JOURNAL:
            return DEVIATION_PATHS.JOURNAL;
        case DeviationTypes.COMMISSION:
            return DEVIATION_PATHS.COMMISSION;
        case DeviationTypes.POLL:
            return DEVIATION_PATHS.POLL;
        case DeviationTypes.STATUS:
            return DEVIATION_PATHS.STATUS;
        case DeviationTypes.TIER:
            return DEVIATION_PATHS.TIER;
        default:
            return DEVIATION_PATHS.DEVIATION;
    }
}
export function getDeviationUrl(deviation) {
    var deviationId = deviation.deviationId, deviationTitle = deviation.title, author = deviation.author, url = deviation.url;
    // on dev, we always want to use generated urls because otherwise
    // devpage inner links will lead to green
    // TODO why not just generate all urls???
    if (!url ||
        (!/^https:\/\/www.deviantart.com\//.test(url) &&
            !/^https:\/\/sta.sh\//.test(url)) ||
        (process.env.NODE_ENV !== 'test' && process.env.NODE_ENV !== 'production')) {
        return Url.deviationLink(deviationId, author && author.username, deviationTitle, getDeviationBasePath(deviation));
    }
    return url;
}
export function getDeviationPath(deviation) {
    var deviationId = deviation.deviationId, deviationTitle = deviation.title, author = deviation.author;
    return Url.deviationPath(deviationId, author && author.username.toLowerCase(), deviationTitle, getDeviationBasePath(deviation));
}
export function isValidFileSrc(src) {
    return (/^https:\/\/api-da\.wixmp\.com\//.test(src) ||
        /^https:\/\/(.+)\.deviantart\.net\//.test(src) ||
        /^https:\/\/(|images-)wixmp-ed30a86b8c4ca887773594c2\.wixmp\.com\//.test(src) ||
        /^https:\/\/(|images-)wixmp-395331243bc2089e8c09c8d3\.wixmp\.com\//.test(src));
}
export function getFilePixelSize(file) {
    return (file.width || 0) * (file.height || 0);
}
export function isFileLargerThanSize(file, width, height) {
    if (width && height && file.width && file.height) {
        return file.width > width || file.height > height;
    }
    else if (width && file.width) {
        return file.width > width;
    }
    else if (height && file.height) {
        return file.height > height;
    }
    return false;
}
export function isFileLargerThanSizeOrEqual(file, width, height) {
    if (width && height && file.width && file.height) {
        return file.width >= width || file.height >= height;
    }
    else if (width && file.width) {
        return file.width >= width;
    }
    else if (height && file.height) {
        return file.height >= height;
    }
    return false;
}
export function isFileTiny(file, isMobile) {
    if (!file.width || !file.height) {
        return false;
    }
    var thresholdWidth = isMobile
        ? TINY_IMAGE_THRESHOLD_WIDTH_MOBILE
        : TINY_IMAGE_THRESHOLD_WIDTH;
    var thresholdHeight = isMobile
        ? TINY_IMAGE_THRESHOLD_HEIGHT_MOBILE
        : TINY_IMAGE_THRESHOLD_HEIGHT;
    return file.width < thresholdWidth && file.height < thresholdHeight;
}
export function scaleToSize(content, container) {
    // if anything is missing, bail
    if (!content.width ||
        !content.height ||
        !container.width ||
        !container.height) {
        return;
    }
    // if our content is small enough, noop
    if (content.width <= container.width && content.height <= container.height) {
        return { width: content.width, height: content.height };
    }
    // we need to check along which axis do we need to scale more (ie. smaller scale factor)
    var widthScale = container.width / content.width;
    var heightScale = container.height / content.height;
    if (widthScale < heightScale) {
        return {
            width: container.width,
            height: content.height * widthScale,
        };
    }
    return {
        width: content.width * heightScale,
        height: container.height,
    };
}
export function getIsShortStatus(_a, ignoreMedia) {
    var textContent = _a.textContent;
    if (ignoreMedia === void 0) { ignoreMedia = false; }
    if (!textContent) {
        return false;
    }
    var isLegacy = textContent.html.type === 'writer';
    var hasMedia = false;
    if (!isLegacy && !ignoreMedia) {
        var mediaCounts = getFeature(MEDIA_COUNT_FEATURE, textContent.html.features);
        var _b = (mediaCounts === null || mediaCounts === void 0 ? void 0 : mediaCounts.data) || {}, _c = _b.images, images = _c === void 0 ? 0 : _c, _d = _b.video, video = _d === void 0 ? 0 : _d, _e = _b.previews, previews = _e === void 0 ? 0 : _e;
        if (images > 0 || video > 0 || previews > 0) {
            hasMedia = true;
        }
    }
    return textContent.excerpt.length < CHARS_BEFORE_LONG_STATUS && !hasMedia;
}
export function getStatusImage(deviation) {
    var backgroundItem;
    if (deviation.textContent.html.type === 'writer') {
        var statusItems = (deviation.status && deviation.status.items) || [];
        var bg = statusItems.find(function (item) { return item.type === 'thumb_background_deviation'; });
        backgroundItem = (bg && bg.deviation) || undefined;
    }
    else {
        var first = getFeature(FIRST_IMAGE_FEATURE, deviation.textContent.html.features);
        if (first) {
            backgroundItem = first.data.deviation;
        }
    }
    return backgroundItem;
}
export function getStatusBackgroundProps(deviation) {
    var isShortStatus = getIsShortStatus(deviation);
    var image = getStatusImage(deviation);
    var defaultProps = isShortStatus && image
        ? {
            image: image,
            backgroundType: BackgroundType.IMAGE,
            imageDisplayMode: BackgroundImageDisplayMode.COVER,
        }
        : undefined;
    return get(deviation, 'status.background', defaultProps);
}
export function getTranslatedDeviationTitle(deviation, t) {
    var deviationTitle = getDeviationTitle(deviation);
    var author = deviation.author;
    if (!author) {
        return deviationTitle;
    }
    if (getDeviationType(deviation) === DeviationTypes.STATUS) {
        return t('deviation.deviationTitle.status', { author: author.username });
    }
    return t('deviation.deviationTitle', {
        title: deviationTitle,
        author: author.username,
    });
}
export function deviationTypeToTranslationKey(type) {
    var _a;
    var typeMap = (_a = {},
        _a[DeviationTypes.COMMISSION] = 'commission',
        _a[DeviationTypes.POLL] = 'poll',
        _a[DeviationTypes.STATUS] = 'status',
        _a[DeviationTypes.JOURNAL] = 'journal',
        _a[DeviationTypes.LITERATURE] = 'literature',
        _a);
    return typeMap.hasOwnProperty(type)
        ? "common.".concat(typeMap[type])
        : "common.deviation";
}
export function getSource(media) {
    if (!media || media.length === 0) {
        return null;
    }
    // TODO: pick the size that fits the screen size the best
    // get the largest quality available for now
    var maxQuality = media
        .map(function (file) { return parseInt("".concat(file.quality), 10); })
        .sort(function (a, b) { return b - a; })[0];
    return media.find(function (file) {
        return file.quality ? file.quality.startsWith("".concat(maxQuality)) : false;
    });
}
export function getBlurredThumbStyles(deviation) {
    // TODO: use actual blurred thumb image, this is fake
    var thumb = DeviationMediaService.getSmallestThumb(deviation);
    if (!thumb) {
        return;
    }
    return {
        backgroundImage: "url('".concat(thumb.src, "')"),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflow: 'hidden',
        filter: 'blur(25px)',
    };
}
export function getSuggestionReasonsByPriority(deviation) {
    var priority = [
        DeviationSuggestionReasons.Boosted,
        DeviationSuggestionReasons.DailyDeviation,
        DeviationSuggestionReasons.WatchingUser,
        DeviationSuggestionReasons.WatchingGroup,
        DeviationSuggestionReasons.WatchingTopic,
        DeviationSuggestionReasons.Recommended,
        DeviationSuggestionReasons.NewDeviant,
        DeviationSuggestionReasons.Popular,
    ];
    // just filter
    return priority.filter(function (reason) { var _a; return (_a = deviation.suggestedReasons) === null || _a === void 0 ? void 0 : _a.includes(reason); });
}
export var TextDumboType = {
    ANTFART: 'antfart',
    NANO: 'nano',
    MICRO: 'micro',
    TINY: 'tiny',
    SMALL: 'small',
    MEDIUM: 'medium',
    BIG: 'big',
};
export var TextDumboTypeSize = {
    ANTFART: 66,
    NANO: 150,
    MICRO: 170,
    TINY: 250,
    SMALL: 275,
    MEDIUM: 400,
    BIG: 600,
};
export function getTextDumboTypeBySize(size) {
    if (size <= TextDumboTypeSize.ANTFART) {
        return TextDumboType.ANTFART;
    }
    if (size <= TextDumboTypeSize.NANO) {
        return TextDumboType.NANO;
    }
    if (size <= TextDumboTypeSize.MICRO) {
        return TextDumboType.MICRO;
    }
    if (size <= TextDumboTypeSize.TINY) {
        return TextDumboType.TINY;
    }
    if (size >= TextDumboTypeSize.BIG) {
        return TextDumboType.BIG;
    }
    if (size >= TextDumboTypeSize.MEDIUM) {
        return TextDumboType.MEDIUM;
    }
    return TextDumboType.SMALL;
}
/** Returns the tier this deviation is available under (if any) */
export function getPrimaryTier(deviation) {
    return deviation === null || deviation === void 0 ? void 0 : deviation.primaryTier;
}
/** Is the current user subscribed to the tier this deviation is part of? */
export function getIsUserSubscribed(deviation) {
    var _a, _b;
    return !!((_b = (_a = getPrimaryTier(deviation)) === null || _a === void 0 ? void 0 : _a.tier) === null || _b === void 0 ? void 0 : _b.isUserSubscribed);
}
/** Tier desciprion != deviation description */
export function getTierDescription(deviation) {
    var _a;
    return (_a = deviation === null || deviation === void 0 ? void 0 : deviation.textContent) === null || _a === void 0 ? void 0 : _a.excerpt;
}
export var getTranslatedTierInfo = function (deviation, t) {
    var _a, _b, _c;
    if (((_a = deviation === null || deviation === void 0 ? void 0 : deviation.tier) === null || _a === void 0 ? void 0 : _a.state) === 'pending_deletion') {
        return deviation.tier.isUserSubscribed
            ? t('deviationview.tierCard.info.pending_deletion.subscribed')
            : t('deviationview.tierCard.info.pending_deletion.unsubscribed');
    }
    switch ((_c = (_b = deviation === null || deviation === void 0 ? void 0 : deviation.tier) === null || _b === void 0 ? void 0 : _b.settings) === null || _c === void 0 ? void 0 : _c.accessSettings) {
        case 'all':
            return t('deviationview.tierCard.access.all');
        case 'future_only':
            return t('deviationview.tierCard.access.future_only');
        case 'limited_past_and_future':
            return t('deviationview.tierCard.access.limited_past_and_future');
        default:
            return undefined;
    }
};
export function getShouldDisplayPaywall(deviationType) {
    return ![
        DeviationTypes.TIER,
        DeviationTypes.JOURNAL,
        DeviationTypes.STATUS,
        DeviationTypes.LITERATURE,
    ].includes(deviationType);
}
export function isDeviation(item) {
    return item.hasOwnProperty('deviationId');
}
/** Provides a unified way of accessing related content between old and new format. Do NOT use for rendering purposes */
export function getRelatedContentBlock(deviation, type) {
    var _a, _b;
    return (_b = (_a = deviation.extended) === null || _a === void 0 ? void 0 : _a.relatedContent) === null || _b === void 0 ? void 0 : _b.find(function (block) { return block.contentType === type; });
}
/** Returns true if we expect/allow the deviation to have pcp */
export function getCanHavePremiumDownload(deviation) {
    var deviationType = getDeviationType(deviation);
    return [
        // todo?: DeviationTypes.PDF, DeviationTypes.FILM,
        DeviationTypes.IMAGE,
    ].includes(deviationType);
}
//# sourceMappingURL=_helpers.js.map